import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { IVideo } from "../hooks/useFetchVideos";
import { MenuDrawer } from "../../../../components/MenuOptions";
import { SettingsVideoModal } from "../selectedVideoCardV2/SettingsVideoModal";
import { IBrand } from "../../../brand/team/data/type";
import { AddToStoryModalV2 } from "../selectedVideoCardV2/AddToStoryModalV2";
import axiosInstant from "../../../../api";
import {
    removeVideoFromEmbed,
    deleteVideo,
    getBrandByVideoId,
    unapproveBrandProjectFile,
    reimportFile
} from "../../../../api/endpoints";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog/ConfirmationDialog";
import { isAM, canApproveUnapproveVideos, isInternal } from "../../../../utils";
import { useUser } from "../../../../hooks/useUser";
import { VariationReviewModal } from "../../../videoProduction/components/videoReview/VariationReviewModal";
import { PreviousVersionsModal } from "../../../videoProduction/components/videoReview/PreviousVersionsModal";
import { canViewPreviousVersions } from "../../../videoProduction/components/videoReview/canViewPreviousVersions";
import AddToVideoShortlistMenu from "../../VideoShortlists/AddToVideoShortlistMenu";
import { buildCopyPath } from "./buildCopyPath";
import { pathHasSegment } from "../adminVideosV2/helper";

interface Props {
    video: IVideo;
    brand?: IBrand;
    refetch: () => void;
    onMenuClose?: () => void;
    isDetailedPage?: boolean;
    onClose?: () => void;
    project?: any;
    onRemoveFromVideoShortlist?: () => void;
}

export function VideoCardMenuV2(props: Props) {
    const { video, brand, refetch, onMenuClose, isDetailedPage, onClose, project } = props;
    const { role } = useUser();
    const isInternalUser = isInternal(role);
    const hasFile = !!(video.key || video.s3Location);

    const [searchParams] = useSearchParams();
    const [embedId, setEmbedId] = useState(0);
    const [showSettingsModal, setShowSettingsModal] = React.useState(false);
    const [addVideoDialogState, setAddVideoDialogState] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showUnapproveConfirmation, setShowUnapproveConfirmation] = useState(false);
    const [showVariationReview, setShowVariationReview] = useState(false);
    const [showPreviousVersions, setShowPreviousVersions] = useState(false);
    const [fetchedBrand, setFetchedBrand] = useState<IBrand | null>(null);
    const [showShortlistModal, setShowShortlistModal] = useState(false);

    const handleClose = () => {
        setAddVideoDialogState(false);
        refetch();
    };

    const removeVideoFromGroup = async () => {
        if (embedId) {
            await axiosInstant.delete(removeVideoFromEmbed(embedId, video.id));
            onClose?.();
        }
    };

    const handleDeleteVideo = async () => {
        try {
            await axiosInstant.delete(deleteVideo(video.id.toString()));
            toast.success("Video deleted successfully");
            setShowDeleteConfirmation(false);
            refetch();
            onClose?.();
        } catch (error) {
            toast.error("Failed to delete video. Please try again.");
        }
    };

    useEffect(() => {
        const embed = searchParams.get("embed");
        if (embed) setEmbedId(parseInt(embed, 10));
    }, [searchParams]);

    const handleReviewVideo = () => {
        setShowVariationReview(true);
    };

    const videoBrand = brand || video?.brandOwners?.[0] || fetchedBrand;

    const handleUnapproveVideo = async () => {
        try {
            const projectId = props.project?.id;
            // Newer videos don't carry a brand directly — that's only on older ones.
            // Pull the brand from the project (first one for now; a project can have several).
            const brandId = props.project?.brands?.[0]?.id ?? videoBrand?.id;
            if (!brandId || !projectId) {
                toast.error("Please open this video from its project page to unapprove it.");
                return;
            }

            await axiosInstant.patch(unapproveBrandProjectFile(brandId, projectId, video.id));
            toast.success("Video unapproved successfully!");
            refetch();
            onClose?.();
        } catch (error) {
            console.error("Failed to unapprove video:", error);
            toast.error("Failed to unapprove video. Please try again.");
        }
    };

    const handleOpenSettings = async () => {
        if (!isInternalUser && !video.final) {
            return;
        }

        if (videoBrand) {
            setShowSettingsModal(true);
        } else if (!fetchedBrand) {
            try {
                const endpoint = getBrandByVideoId(String(video.id));
                const response = await axiosInstant.get(endpoint);
                const { data } = response;
                if (data) {
                    setFetchedBrand(data);
                    setShowSettingsModal(true);
                } else {
                    toast.error("Unable to load video settings. Brand information not found.");
                }
            } catch (error) {
                toast.error("Unable to load video settings. Please try again.");
            }
        }
    };

    const handleCopyPath = async () => {
        const path = buildCopyPath(video, props.project);
        try {
            await navigator.clipboard.writeText(path);
            toast.success("Path copied to clipboard");
        } catch {
            const ta = document.createElement("textarea");
            ta.value = path;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            toast.success("Path copied to clipboard");
        }
        onClose?.();
    };

    const canCopyPath = role === "POST_PRODUCTION_ADMIN" || role === "ADMIN" || isAM(role);

    const handleReimport = async () => {
        try {
            await axiosInstant.get(reimportFile(video.id));
            toast.success("Re-import started — the file will refresh once processing completes.");
        } catch (error: any) {
            const status = error?.response?.status;
            const code = error?.response?.data?.code;
            if (status === 404) {
                toast.error("File no longer exists.");
            } else if (status === 400 && code === "FileMissingPath") {
                toast.error("File hasn't been delivered yet — nothing to re-import.");
            } else if (status >= 500) {
                toast.error("Re-import failed, please try again.");
            } else {
                toast.error("Re-import failed.");
            }
        }
        onClose?.();
    };

    const canReimport = isAM(role);

    const projectId = props.project?.id || video.projectId || (video as any).project?.id;
    const hasPreviousVersions = !!projectId && hasFile && canViewPreviousVersions(role, video);

    return (
        <>
            <MenuDrawer
                title="video"
                id={video.id}
                isDetailedPage={isDetailedPage}
                onMenuClose={onMenuClose}
                showDelete={false}
                downloadUrl={hasFile ? video?.location || video?.reviewS3File?.location : undefined}
                onAddToStory={hasFile && !isInternalUser ? () => setAddVideoDialogState(true) : undefined}
                onEdit={hasFile && (isInternalUser || video.final) ? handleOpenSettings : undefined}
                showUnapprove={
                    hasFile && isInternalUser && !!video.final && !!props.project?.id && canApproveUnapproveVideos(role)
                }
                onUnapprove={
                    hasFile && isInternalUser && !!video.final && !!props.project?.id && canApproveUnapproveVideos(role)
                        ? () => setShowUnapproveConfirmation(true)
                        : undefined
                }
                unapproveLabel="Unapprove video"
                onRemoveFromStory={hasFile && embedId ? removeVideoFromGroup : undefined}
                onDelete={hasFile && isAM(role) ? () => setShowDeleteConfirmation(true) : undefined}
                showReviewVideo={hasFile && !video.final}
                onReviewVideo={hasFile ? handleReviewVideo : undefined}
                showDownloadOptions={false}
                onAddToVideoShortlist={
                    hasFile && isInternalUser && !pathHasSegment(window.location.pathname, "shortlists")
                        ? () => setShowShortlistModal(true)
                        : undefined
                }
                onRemoveFromVideoShortlist={
                    isInternalUser ? props.onRemoveFromVideoShortlist : undefined
                }
                onCopyPath={canCopyPath ? handleCopyPath : undefined}
                onReimport={hasFile && canReimport ? handleReimport : undefined}
                onViewPreviousVersions={
                    hasPreviousVersions ? () => setShowPreviousVersions(true) : undefined
                }
            />
            {showSettingsModal && videoBrand && (
                <SettingsVideoModal
                    video={video}
                    brand={videoBrand}
                    onClose={() => setShowSettingsModal(false)}
                    onComplete={() => refetch?.()}
                />
            )}
            {brand && (
                <AddToStoryModalV2
                    open={addVideoDialogState}
                    onClose={handleClose}
                    brandId={brand.id?.toString() || ""}
                    videoId={video.id}
                />
            )}
            <ConfirmationDialog
                open={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={handleDeleteVideo}
                title="Delete Video"
                description="Are you sure you want to delete this video? This action cannot be undone."
                confirmLabel="Delete"
                cancelLabel="Cancel"
            />
            <ConfirmationDialog
                open={showUnapproveConfirmation}
                onClose={() => setShowUnapproveConfirmation(false)}
                onConfirm={() => {
                    setShowUnapproveConfirmation(false);
                    handleUnapproveVideo();
                }}
                title="Unapprove Video"
                description="Are you sure you want to unapprove this video?"
                confirmLabel="Unapprove"
                cancelLabel="Cancel"
            />
            <AddToVideoShortlistMenu
                open={showShortlistModal}
                onClose={() => setShowShortlistModal(false)}
                s3FileId={video.id}
            />
            {showPreviousVersions && projectId && (
                <PreviousVersionsModal
                    projectId={projectId}
                    file={video}
                    project={project}
                    onClose={() => setShowPreviousVersions(false)}
                />
            )}
            {showVariationReview && (
                <VariationReviewModal
                    open={showVariationReview}
                    onClose={() => {
                        setShowVariationReview(false);
                        refetch();
                    }}
                    project={props.project || video.project}
                    variationFile={video}
                    onApproved={() => {
                        setShowVariationReview(false);
                        refetch();
                    }}
                />
            )}
        </>
    );
}
